import type { IconName } from '../models/types';
import { renderIcon } from './icons';

/**
 * Alt menü (tab bar).
 *
 * Bilgi mimarisi CLAUDE.md §6'da: 6 sekme var ve Anasayfa,
 * diğerlerinden görsel olarak ayrılır — düz bir ikon yerine turuncu
 * gradyanlı, hafifçe yukarı taşmış dairesel bir düğmedir. Amaç,
 * uygulamanın "eve dön" hareketini tek bakışta bulunur kılmak.
 */

/**
 * Uygulamadaki gezinilebilir ekranlar.
 *
 * Bunların yalnızca 6'sının alt menüde sekmesi var; geri kalanlar
 * bir sekmenin ALT ekranlarıdır (`SUB_SCREEN_PARENTS`).
 */
export type RouteId =
  | 'home'
  | 'streak'
  | 'cleaning'
  | 'cleaningEdit'
  | 'challenge'
  | 'sport'
  | 'todo'
  | 'profile'
  | 'habitEdit'
  | 'rewards'
  | 'history';

interface TabDefinition {
  route: RouteId;
  label: string;
  icon: IconName;
}

/** Sekmelerin ekrandaki soldan sağa sırası. */
const TABS: TabDefinition[] = [
  { route: 'home', label: 'Anasayfa', icon: 'home' },
  { route: 'cleaning', label: 'Temizlik', icon: 'broom' },
  { route: 'challenge', label: 'Challenge', icon: 'trophy' },
  { route: 'sport', label: 'Spor', icon: 'dumbbell' },
  { route: 'todo', label: 'Yapılacak', icon: 'checklist' },
  { route: 'profile', label: 'Profil', icon: 'user' },
];

/**
 * Alt ekranların hangi sekmeye ait olduğu.
 *
 * Bu ekranların alt menüde kendi düğmesi yoktur; bir sekmenin
 * içinden açılırlar. Oradayken, geldikleri sekme yanık kalmalı ki
 * kullanıcı uygulamanın neresinde olduğunu kaybetmesin.
 */
const SUB_SCREEN_PARENTS: Partial<Record<RouteId, RouteId>> = {
  streak: 'home',
  cleaningEdit: 'cleaning',
  habitEdit: 'profile',
  rewards: 'profile',
  history: 'profile',
};

/** Hangi sekmenin "aktif" görüneceğini belirler. */
function resolveActiveTab(route: RouteId): RouteId {
  return SUB_SCREEN_PARENTS[route] ?? route;
}

/**
 * Alt menüyü oluşturur ve sekme tıklamalarını `onNavigate`'e bağlar.
 *
 * Dönen eleman çağıran tarafından DOM'a eklenir. Sekme değiştiğinde
 * menü yeniden oluşturulmaz; sadece `updateTabBarActiveState` ile
 * aktif işaret güncellenir — böylece her gezinmede gereksiz DOM
 * yıkıp yeniden kurma olmaz.
 */
export function createTabBar(
  activeRoute: RouteId,
  onNavigate: (route: RouteId) => void,
): HTMLElement {
  const nav = document.createElement('nav');
  nav.className = 'tab-bar';
  nav.setAttribute('aria-label', 'Ana gezinme');

  for (const tab of TABS) {
    const isHome = tab.route === 'home';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = isHome ? 'tab tab--home' : 'tab';
    button.dataset['route'] = tab.route;
    // Anasayfa düğmesinde metin etiketi yok (tasarım gereği sadece
    // daire), bu yüzden ekran okuyucular için adını ayrıca veriyoruz.
    button.setAttribute('aria-label', tab.label);

    if (isHome) {
      button.innerHTML = `
        <span class="tab__home-circle">${renderIcon(tab.icon, { size: 18, color: 'white' })}</span>
      `;
    } else {
      button.innerHTML = `
        <span class="tab__icon">${renderIcon(tab.icon, { size: 21 })}</span>
        <span class="tab__label">${tab.label}</span>
      `;
    }

    button.addEventListener('click', () => onNavigate(tab.route));
    nav.appendChild(button);
  }

  updateTabBarActiveState(nav, activeRoute);
  return nav;
}

/**
 * Aktif sekmeyi işaretler.
 *
 * `aria-current="page"` hem ekran okuyuculara hangi sayfada
 * olunduğunu söyler, hem de CSS'te aktif rengi seçmek için kullanılır
 * — böylece görsel durum ile erişilebilirlik bilgisi tek yerden gelir
 * ve birbirinden sapamaz.
 */
export function updateTabBarActiveState(nav: HTMLElement, route: RouteId): void {
  const activeTab = resolveActiveTab(route);

  for (const button of nav.querySelectorAll<HTMLElement>('.tab')) {
    if (button.dataset['route'] === activeTab) {
      button.setAttribute('aria-current', 'page');
    } else {
      button.removeAttribute('aria-current');
    }
  }
}
